import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { IntakeSubmission } from '@/api/intake/intakeApi.types';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { PageContainer } from '@/components/layout/PageContainer';
import { Icon } from '@/components/common/Icon';
import { IntakeStatusBadge } from '@/components/common/IntakeStatusBadge';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { IntakeReviewActions } from '@/components/practitioner/shared/IntakeReviewActions';
import { IntakeSubmissionDetails } from '@/components/practitioner/shared/IntakeSubmissionDetails';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useWorkspaceIntakeSubmissionQuery } from '@/hooks/useIntake';
import { usePatientQuery } from '@/hooks/usePatientsQueries';

export function IntakeSubmissionPage() {
  const { t } = useTranslation('intake');
  const navigate = useNavigate();
  const { submissionId } = useParams();
  const submissionQuery = useWorkspaceIntakeSubmissionQuery(
    submissionId === undefined ? '' : submissionId
  );
  const submission = submissionQuery.data;

  return (
    <PageContainer width="full">
      <button
        type="button"
        onClick={function back() {
          navigate(APPLICATION_ROUTES.workspaceIntake);
        }}
        className="text-sm font-semibold text-zoomuted hover:text-zooindigo flex items-center gap-1 mb-4"
      >
        <Icon name="ArrowLeft" /> {t('inbox.back')}
      </button>

      {submissionQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : submissionQuery.isError === true || submission === undefined ? (
        <QueryErrorState
          onRetry={function retry() {
            submissionQuery.refetch();
          }}
        />
      ) : (
        <Card padding="lg" className="space-y-5">
          <IntakeSubmissionHeader submission={submission} />
          {submission.patientId === null ? (
            <IntakeSubmissionDetails submission={submission} patient={null} />
          ) : (
            <LinkedPatientIntakeDetails submission={submission} patientId={submission.patientId} />
          )}
        </Card>
      )}
    </PageContainer>
  );
}

function IntakeSubmissionHeader({ submission }: { submission: IntakeSubmission }) {
  const { t } = useTranslation('intake');
  const navigate = useNavigate();
  const petName =
    submission.proposedPetName === null ? t('inbox.unnamedPet') : submission.proposedPetName;

  return (
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold text-zooindigo truncate">
            {submission.patientId === null ? petName : t('inbox.detailTitle')}
          </h1>
          <IntakeStatusBadge status={submission.status} />
        </div>
        <p className="text-sm text-zoomuted mt-1">
          {t('inbox.fromClient', { clientName: submission.clientName })}
        </p>
        {submission.patientId === null ? (
          <p className="text-xs text-zoomuted mt-1">{t('inbox.newPetHint')}</p>
        ) : null}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          variant="outline"
          size="chip"
          onClick={function openClient() {
            navigate(
              generatePath(APPLICATION_ROUTES.workspaceClientDetail, {
                clientId: submission.clientId,
              })
            );
          }}
        >
          <Icon name="User" />
          {t('inbox.openClient')}
        </Button>
        {submission.patientId === null ? null : (
          <Button
            variant="outline"
            size="chip"
            onClick={function openPatient() {
              if (submission.patientId !== null) {
                navigate(
                  generatePath(APPLICATION_ROUTES.workspacePatientDetail, {
                    patientId: submission.patientId,
                  })
                );
              }
            }}
          >
            <Icon name="PawPrint" />
            {t('inbox.openPatient')}
          </Button>
        )}
        <IntakeReviewActions submission={submission} />
      </div>
    </div>
  );
}

interface LinkedPatientIntakeDetailsProps {
  submission: IntakeSubmission;
  patientId: string;
}

function LinkedPatientIntakeDetails({ submission, patientId }: LinkedPatientIntakeDetailsProps) {
  const patientQuery = usePatientQuery(patientId);
  if (patientQuery.isLoading === true) {
    return (
      <div className="py-8 flex justify-center">
        <LoadingSpinner className="w-6 h-6" />
      </div>
    );
  }
  const patient = patientQuery.data === undefined ? null : patientQuery.data;
  return <IntakeSubmissionDetails submission={submission} patient={patient} />;
}
